/* eslint-disable class-methods-use-this */

'use strict';

import Carousel from 'org/components/common/Carousel';

var layout = require('org/layout').init();

/**
 * PDP photo gallery
 *
 * Everything the gallery does - looping, the thumbnail rail and its active state, the mobile
 * pagination dots - is delegated to Swiper through the "pdpGallery" entry in
 * cartridge/config/carousel.json. The thumbs rail is declared as a Swiper parameter object, so
 * Swiper builds, syncs and tears down that second instance on its own.
 *
 * What is left here is the handful of things Swiper cannot know about: keeping the thumb rail's
 * markup in step with the main slides after Product.js swaps the image set (colour change or
 * "See it in a size like mine"), skipping the rail entirely on mobile, and resolving the model
 * caption and the 360 video from the active slide.
 */
export default class PdpGallery extends Carousel {
    init() {
        this.$gallery = this.$el.closest('.js-pdp-gallery');
        this.$thumbs = $('.js-pdp-gallery-thumbs', this.$gallery);
        this.initConfig({});

        if (!this.eligibleOnDevice() || !this.hasSlides()) {
            this.initializationDeclined();
            return;
        }

        this.thumbsConfig = this.config.thumbs;
        this.prepareConfig();
        this.initCarousel();

        this.event('pdpGallery:teardown', this.destroyCarousel.bind(this), this.$el);
        this.event('pdpGallery:update', this.onImagesUpdated.bind(this), this.$el);
        this.event('carousel:reinit', this.reInit.bind(this), $('body'));
        this.eventDelegate('keyup', '.b-product_carousel-control', this.onControlKeyup.bind(this));
        this.eventMgr('window.modechanged', this.onModeChanged.bind(this));
    }

    /**
     * Keeps one Enter press on an arrow to one photo.
     * @param {jQuery.Event} event - keyup on an arrow
     */
    onControlKeyup(event) {
        if (event.keyCode === 13) {
            event.stopPropagation();
        }
    }

    /**
     * Attaches only the per-instance Swiper events, leaving the carousel:reinit subscription to
     * init().
     *
     * The inherited version subscribes a fresh anonymous body handler for carousel:reinit every
     * time it runs, and nothing ever unbinds it - destroying the Swiper disposes the instance,
     * not the listener. This gallery re-initialises on every colour change, fit-model change and
     * viewport-mode change, so those handlers would pile up and a single carousel:reinit from
     * elsewhere on the PDP would then run reInit() once per past initialisation, each one
     * holding this component and its slides alive.
     *
     * @param {boolean} [attachEvents] - passed through to the autoplay handler
     */
    initCarouselEvents(attachEvents) {
        this.autoplayHandler(attachEvents);
    }

    /**
     * Adjusts the Swiper config to the current viewport and image count:
     * the thumbnail rail is desktop only, so the nested Swiper is dropped below 1024px rather
     * than being initialised against a hidden, zero-height container, and a single photo has
     * nothing to loop between.
     */
    prepareConfig() {
        if (layout.isMobileView()) {
            delete this.config.thumbs;
        } else if (this.thumbsConfig) {
            this.config.thumbs = this.thumbsConfig;
        }

        this.config.loop = this.getSlideCount() > 1;
    }

    getSlideCount() {
        return $('.js-product_carousel-slide', this.$el).length;
    }

    initCarousel() {
        super.initCarousel();

        // Runs after core's own slideChange handler, which resolves the caption from activeIndex
        // and so reads the wrong slide once loop mode reorders them, and after the thumbs module
        // has moved its active class.
        this.swiperInstance.on('slideChange', this.syncActiveSlide.bind(this));
        this.syncActiveSlide();
    }

    syncActiveSlide() {
        this.syncModelSpec();
        this.syncVideoPlayback();
    }

    /**
     * Plays the 360 video only while its slide is the active one, so exactly one copy is ever
     * running, and hands over to native controls if the browser refuses to autoplay rather than
     * leaving the customer looking at a frozen poster frame
     */
    syncVideoPlayback() {
        var activeSlide = this.swiperInstance ? this.swiperInstance.slides[this.swiperInstance.activeIndex] : null;

        $('.js-product-360-video', this.$el).each(function () {
            var video = this;

            if (!activeSlide || !$.contains(activeSlide, video)) {
                video.pause();
                return;
            }

            var playing = video.play();

            if (playing && typeof playing.catch === 'function') {
                playing.catch(function () {
                    video.controls = true;
                });
            }
        });
    }

    /**
     * Core's carousel keys the "model is wearing size X" caption off activeIndex, which stops
     * lining up with the slides' data-index once loop mode reorders them, so the caption is
     * resolved from the active slide element instead.
     */
    syncModelSpec() {
        if (!this.swiperInstance) {
            return;
        }

        var activeSlide = this.swiperInstance.slides[this.swiperInstance.activeIndex];

        $('body').find('.model-specification-content')
            .toggleClass('hide', !!activeSlide && $(activeSlide).attr('data-spec') === 'notSame');
    }

    /**
     * Rebuilds the thumb rail for a new image set and restarts Swiper.
     *
     * Product.js has already replaced the main slides by the time this runs, so the rail is
     * rebuilt against them and takes its URLs from the variation response. The carousel itself
     * was torn down before that replacement, on pdpGallery:teardown.
     *
     * Sizes can carry different numbers of photos, so the config is recomputed from the new
     * slide count and the gallery is put back on the first photo.
     *
     * @param {jQuery.Event} event - pdpGallery:update
     * @param {Array} [images] - the pdpMainDesktop images from the variation response
     */
    onImagesUpdated(event, images) {
        this.destroyCarousel();
        this.renderThumbs(images);
        this.prepareConfig();
        this.initCarousel();
        this.resetToFirstSlide();
    }

    /**
     * Crossing the desktop breakpoint adds or drops the thumbnail rail, which Swiper cannot be
     * told about after the fact, so the carousel is rebuilt. A new instance opens on the first
     * photo, so the one being viewed is carried over by hand.
     */
    onModeChanged() {
        var viewedIndex = this.swiperInstance ? this.swiperInstance.realIndex : 0;

        this.destroyCarousel();
        this.prepareConfig();
        this.initCarousel();
        this.goToSlide(viewedIndex);
    }

    /**
     * Selects a photo without animating.
     *
     * @param {number} index - position among the real slides, ignoring any loop duplicates
     */
    goToSlide(index) {
        if (!this.swiperInstance) {
            return;
        }

        if (this.config.loop) {
            this.swiperInstance.slideToLoop(index, 0);
        } else {
            this.swiperInstance.slideTo(index, 0);
        }
    }

    /**
     * Selects the first slide.
     */
    resetToFirstSlide() {
        this.goToSlide(0);
    }

    /**
     * Redraws the rail against the current main slides.
     *
     * The 360 video takes the first main slide but has no entry in the image set, so photos are
     * counted separately from slides to keep the two lists aligned. Without an image set the
     * rail falls back to the main slides' own URLs.
     *
     * @param {Array} [images] - the pdpMainDesktop images from the variation response
     */
    renderThumbs(images) {
        var $wrapper = $('.swiper-wrapper', this.$thumbs);

        if (!$wrapper.length) {
            return;
        }

        var $thumbs = $();
        var photoIndex = 0;

        $('.js-product_carousel-slide', this.$el).each(function () {
            var $slide = $(this);
            var $video = $slide.find('.js-product-360-video');
            var $image = $slide.find('.b-product_carousel-image');
            var isVideo = $video.length > 0;
            var src = isVideo ? $video.attr('poster') : $image.attr('src');

            if (!isVideo) {
                var photo = images && images[photoIndex];

                if (photo && photo.thumbURL) {
                    src = photo.thumbURL;
                }

                photoIndex += 1;
            }

            if (!src) {
                return;
            }

            var $thumb = $('<div/>', {
                class: 'b-pdp_gallery-thumbs-slide js-pdp-gallery-thumb swiper-slide' + (isVideo ? ' m-video' : '')
            });

            $thumb.append($('<img/>', {
                class: 'b-pdp_gallery-thumbs-image',
                src: src,
                alt: isVideo ? '' : ($image.attr('alt') || ''),
                loading: 'lazy'
            }));

            $thumbs = $thumbs.add($thumb);
        });

        $wrapper.empty().append($thumbs);
    }
}
